import {
  BadRequestError,
  ConflictError,
  NetworkError,
  NotFoundError,
  RateLimitError,
  ServerError,
} from '@/lib/api/errors/api-errors';
import { NotAuthenticatedError } from '@/lib/api/errors/not-authenticated-error';

import { ForbiddenError } from '../errors/forbidden-error';

interface ApiResponseEnvelope<T> {
  success: boolean;
  message: string;
  data: T;
  statusCode: number;
}

export const handleResponse = async <T>(res: Response): Promise<T> => {
  try {
    if (res.ok) {
      if (res.status === 204) {
        return undefined as unknown as T;
      }
      // 2. 🟢 GLOBAL FIX: Check if it's a wrapper and return the inner data
      const json = await res.json();

      // Check if this looks like your standard ServiceResponse
      if (
        json &&
        typeof json === 'object' &&
        'data' in json &&
        'success' in json
      ) {
        // Return the inner 'data' as the actual type T
        return (json as ApiResponseEnvelope<T>).data;
      }

      // If it's not wrapped (maybe an external API?), return as is
      return json as T;
    }

    if (res.status === 429) {
      let data: {
        message?: string;
        daysRemaining?: number;
        nextAllowedDate?: string;
      };
      try {
        data = await res.json();
      } catch {
        throw new RateLimitError(
          'Rate limit exceeded. Please try again later.',
        );
      }
      const message =
        data.message || 'Rate limit exceeded. Please try again later.';
      const error = new RateLimitError(message);
      if (data.daysRemaining !== undefined) {
        error.daysRemaining = data.daysRemaining;
      }
      if (data.nextAllowedDate) {
        error.nextAllowedDate = data.nextAllowedDate;
      }
      throw error;
    }

    let data: { message?: string; error?: string };
    try {
      data = await res.json();
    } catch (parseError) {
      console.error('Failed to parse error response:', parseError);
      throw new ServerError(`API request failed with status ${res.status}`);
    }

    const message = data.message || data.error || undefined;

    switch (res.status) {
      case 400:
        throw new BadRequestError(message);
      case 401:
        throw new NotAuthenticatedError(message);
      case 403:
        throw new ForbiddenError(message);
      case 404:
        throw new NotFoundError(message);
      case 409:
        throw new ConflictError(message);
      case 500:
      case 502:
      case 503:
      case 504:
        throw new ServerError(message);
      default:
        throw new ServerError(`API request failed with status ${res.status}`);
    }
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new NetworkError();
    }
    if (
      error instanceof ServerError ||
      error instanceof RateLimitError ||
      error instanceof NetworkError ||
      error instanceof NotFoundError ||
      error instanceof NotAuthenticatedError ||
      error instanceof BadRequestError ||
      error instanceof ConflictError ||
      error instanceof ForbiddenError
    ) {
      throw error;
    }

    throw new ServerError('An unexpected error occurred');
  }
};
